import React, { useEffect, useState } from 'react';
import { NearDataProps } from '../../data';
import styled from 'styled-components';
import { SortOption } from '../../../components/list/data';
import LoanInfoService from '../../../../service/LoanInfo';
import Pagination from '../../../components/pagination/Pagination';
import { formatDate3 } from '../../../../func/func';

interface Props {
  iidx: string;
  title: string;
  status: string;
  regionAccess: boolean;
}

const SortOptions: SortOption[] = [
  {
    value: 'AD',
    label: '감정가 높은순',
  },
  {
    value: 'AA',
    label: '감정가 낮은순',
  },
  {
    value: 'BD',
    label: '최저가 높은순',
  },
  {
    value: 'BA',
    label: '최저가 낮은순',
  },
  {
    value: 'CA',
    label: '매각기일 빠른순',
  },
  {
    value: 'CD',
    label: '매각기일 늦은순',
  },
  {
    value: 'DD',
    label: '유찰횟수 높은순',
  },
  {
    value: 'DA',
    label: '유찰횟수 낮은순',
  },
  {
    value: 'ED',
    label: '조회수 높은순',
  },
  {
    value: 'EA',
    label: '조회수 낮은순',
  },
];

function NearList({ iidx, regionAccess, title, status }: Props) {
  const [sortOption, setSortOption] = useState<SortOption>({
    value: 'CA',
    label: '매각기일 빠른순',
  });
  const [sortOptionOpen, setSortOptionOpen] = useState(false);

  const [data, setData] = useState<NearDataProps[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [tagExpend, setTagExpend] = useState<number[]>([]);

  const getData = async () => {
    try {
      const result = await LoanInfoService.arroundItem(
        iidx,
        status,
        sortOption.value,
        currentPage,
      );
      setData(result.data.list);
      setTotalPage(result.data.totalCount);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    getData();
  }, [sortOption, currentPage]);

  const renderTags = (tags: string[], index: number, max: number) => {
    const maxVisibleTags = max;
    if (!tags || tags.length === 0) return null;

    if (tagExpend.includes(index)) {
      return (
        <>
          {tags.map((tag, i) => (
            <Tag key={`${index}-${i}`}>{tag}</Tag>
          ))}
          <Tag
            onClick={(e) => {
              e.stopPropagation();
              setTagExpend((prev) => prev.filter((item) => item !== index));
            }}
          >
            <span>접기</span>
            <img
              src="/assets/images/arrow/arrow-down-gray.svg"
              alt=""
              style={{ width: '16px', transform: 'rotate(180deg)' }}
            />
          </Tag>
        </>
      );
    } else {
      const visibleTags = tags.slice(0, maxVisibleTags);
      const remainingCount = tags.length - maxVisibleTags;

      return (
        <>
          {visibleTags.map((tag, i) => (
            <Tag key={`${index}-${i}`}>{tag}</Tag>
          ))}
          {remainingCount > 0 && (
            <Tag
              onClick={(e) => {
                e.stopPropagation();
                setTagExpend([...tagExpend, index]);
              }}
            >
              +{remainingCount}개
            </Tag>
          )}
        </>
      );
    }
  };

  return (
    <>
      {data && data.length > 0 && (
        <Container id={`list-top-${status}`}>
          <TitleBox>
            <Title>{title}</Title>
            <SortSelectWrapper>
              <SortSelect onClick={() => setSortOptionOpen((prev) => !prev)}>
                {sortOption.label}
                <img
                  src="/assets/images/arrow/arrow-down-gray.svg"
                  alt=""
                  width={16}
                  height={16}
                />
              </SortSelect>
              {sortOptionOpen && (
                <SelectDropdown>
                  {SortOptions.map((item) => (
                    <SelectItem
                      onClick={() => {
                        setSortOption(item);
                        setSortOptionOpen(false);
                      }}
                      selected={sortOption.value === item.value}
                    >
                      <span>{item.label}</span>
                      {sortOption.value === item.value && (
                        <img
                          src="/assets/images/check/blue_check_24.svg"
                          alt=""
                        />
                      )}
                    </SelectItem>
                  ))}
                </SelectDropdown>
              )}
            </SortSelectWrapper>
          </TitleBox>
          <Content>
            {data.map((item) => (
              <Item
                key={item.iidx}
                onClick={() => {
                  const url =
                    item.type === 'a'
                      ? `/auction/detail/${item.iidx}`
                      : `/pauction/detail/${item.iidx}`;
                  window.open(url, '_blank');
                }}
              >
                <ItemInfoContent>
                  <ItemInfoBox>
                    <ItemInfoTop>
                      <ItemInfoSub>
                        {item.type === 'a' ? '경매' : '공매'}
                        <Seperator />
                        {item.caseno}
                        <Seperator />
                        {item.court}
                      </ItemInfoSub>
                      <CountBox>
                        <img src="/assets/images/icon/view-gray.svg" alt="" />
                        <span>{item.view_count}회</span>
                      </CountBox>
                    </ItemInfoTop>
                    <Address>{item.address}</Address>
                    {title === '인근 반경 낙찰 물건' && (
                      <DateContent>
                        <span>낙찰일</span>
                        {formatDate3(item.sale_date)}
                      </DateContent>
                    )}
                  </ItemInfoBox>
                  <PriceBox>
                    {item.appraisal_price && (
                      <PriceItem>
                        감정가{' '}
                        <Price>
                          {Number(item.appraisal_price).toLocaleString()}원
                        </Price>
                      </PriceItem>
                    )}
                    {item.min_price && (
                      <PriceItem type="저">
                        최저가{' '}
                        <Price>
                          {Number(item.min_price).toLocaleString()}원
                        </Price>
                      </PriceItem>
                    )}
                    {item.bid_price && item.bid_rate && (
                      <PriceItem type="낙">
                        낙찰가{' '}
                        <Price>
                          {Number(item.bid_price).toLocaleString()}원
                        </Price>
                        <Price>(낙찰가율 {item.bid_rate}%)</Price>
                      </PriceItem>
                    )}
                  </PriceBox>
                </ItemInfoContent>
                <TagBox>
                  <Tag type={item.status}>{item.status}</Tag>
                  {renderTags(
                    [
                      ...(item.viewgray2
                        ? item.viewgray2
                            .replace(/\[|\]/g, '')
                            .split(' / ')
                            .filter((el) => el !== '')
                        : []),
                    ],
                    item.iidx,
                    2,
                  )}
                </TagBox>
              </Item>
            ))}
          </Content>
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalPage / 5)}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
            scrollTargetId={`list-top-${status}`}
          />
        </Container>
      )}
    </>
  );
}

export default NearList;

const Container = styled.div`
  width: 100%;
  padding: 24px 16px;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;

  gap: 16px;
`;

const TitleBox = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const Title = styled.div`
  font-family: Spoqa Han Sans Neo;
  font-size: 16px;
  font-weight: 700;
  color: #171e26;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
  gap: 16px;
  border-bottom: 1px solid #e1e2e4;
`;

const ItemInfoContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`;

const ItemInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
`;

const DateContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;

  color: #222;
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;

  & > span {
    color: #616161;
  }
`;

const ItemInfoTop = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ItemInfoSub = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;

  font-weight: 500;
  font-size: 12px;
  line-height: 145%;
  letter-spacing: -5%;
  color: #616161;
`;

const Seperator = styled.div`
  width: 1px;
  height: 8px;
  background-color: #37383c9c;
`;

const CountBox = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;

  font-weight: 400;
  font-size: 12px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #37383c9c;
`;

const Address = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -5%;
`;

const PriceBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PriceItem = styled.div<{ type?: string }>`
  display: flex;
  flex-direction: row;
  gap: 4px;

  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: ${({ type }) =>
    type === '저' ? '#008AFF' : type === '낙' ? '#F45551' : '#616161'};
`;

const Price = styled.div`
  font-weight: 700;
`;

const TagBox = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.div<{ type?: string }>`
  display: flex;
  flex-direction: row;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 12px;
  line-height: 145%;
  letter-spacing: -5%;
  color: ${({ type }) =>
    type?.trim() === '신건' ? '#1F9E54' : type ? '#F45551' : '#37383C9C'};
  background-color: ${({ type }) =>
    type?.trim() === '신건' ? '#EAF8F2' : type ? '#FFEFEF' : '#70737C14'};
`;

const SortSelectWrapper = styled.div`
  position: relative;
`;

const SortSelect = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  user-select: none;

  font-weight: 500;
  font-size: 12px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #616161;
`;

const SelectDropdown = styled.div`
  position: absolute;
  top: 30px;
  right: 0;
  width: 188px;
  border-radius: 20px;
  padding: 8px;
  border: 1px solid #e1e2e4;
  box-shadow:
    0px 0px 1px 0px #00000014,
    0px 1px 4px 0px #00000014,
    0px 2px 8px 0px #0000001f;

  z-index: 10;
  background-color: #ffffff;
`;

const SelectItem = styled.div<{ selected?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  border-radius: 8px;
  padding: 10px 16px;
  align-items: center;
  cursor: pointer;

  font-weight: ${({ selected }) => (selected ? 700 : 500)};
  font-size: 16px;
  line-height: 145%;
  letter-spacing: -2%;
  color: ${({ selected }) => (selected ? '#008AFF' : '#616161')};

  &:hover {
    background-color: #f0f0f0;
  }
`;
